import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { ExecutionBlockNode } from "@/pages/workflows/execution/ExecutionBlockNode.tsx";
import {
  useCancelWorkflow,
  useWorkflowExecution,
  useWorkflowExecutionMeetings,
} from "@/queries/workflows/workflow-execution.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiWorkflowExecutionBlockDetail,
  ApiWorkflowExecutionBlockStatus,
  ApiWorkflowExecutionMeeting,
  ApiWorkflowExecutionStatus,
  getApiWorkflowExecutionStatusTitle,
} from "@/types/workflows/workflow-execution.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { WorkflowTreeBuilder } from "@/types/workflows/workflow-tree-builder.ts";
import {
  Box,
  Button,
  ConfirmationModal,
  DotSeparator,
  FontAwesomeIcon,
  ListItem,
  MetricTile,
  Page,
  Skeleton,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { useWorkflowExecutions } from "@/pages/workflows/workflow-execution-hook.tsx";

export const WorkflowExecutionDetailsPage = () => {
  const { executionId } = useParams<{ executionId: string }>();
  const id = executionId ? Number(executionId) : undefined;
  const { t } = useTranslation();
  const { renderDateAndTime, renderEmployeeName, formatDurationDatetime } = useFormatter();
  const { openModal } = useModals();

  const { getWorkflowExecutionBlockStatusTag } = useWorkflowExecutions();

  const { data: execution, isLoading: isExecutionLoading } = useWorkflowExecution(id);
  const hasMeetingBlock = (execution?.blocks ?? []).some(
    (b) => b.action_type === ApiWorkflowBlockActionTypes.meeting
  );
  const { data: meetings } = useWorkflowExecutionMeetings(id, hasMeetingBlock);

  const { mutateAsync: cancelWorkflow } = useCancelWorkflow();

  const isLoading = isExecutionLoading;

  const canCancel =
    (execution?.status === ApiWorkflowExecutionStatus.running ||
      execution?.status === ApiWorkflowExecutionStatus.pending) &&
    (execution?.acl?.can_cancel ?? false);

  const tree = useMemo(() => {
    if (!execution) return null;
    return new WorkflowTreeBuilder(execution.workflow_blocks, execution.edges).buildTree();
  }, [execution]);

  const executionBlocksMap = useMemo(() => {
    if (!execution) return new Map<number, ApiWorkflowExecutionBlockDetail>();
    return new Map(execution.blocks.map((b) => [b.workflow_block_id, b]));
  }, [execution]);

  const meetingsMap = useMemo(() => {
    if (!meetings) return new Map<number, ApiWorkflowExecutionMeeting>();
    return new Map(meetings.map((m) => [m.workflow_block_id, m]));
  }, [meetings]);

  const hasFailedBlock = useMemo(
    () =>
      (execution?.blocks ?? []).some((b) => b.status === ApiWorkflowExecutionBlockStatus.failed),
    [execution]
  );

  useTitle(execution?.workflow_title ?? t("workflow:execution_details"), t("workflow:workflows"));

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.workflows.overviewViewed);
  });

  const duration =
    execution?.datetime_started_at && execution?.datetime_completed_at
      ? formatDurationDatetime(execution.datetime_started_at, execution.datetime_completed_at)
      : "-";
  const executionEmployee = execution?.employee ?? null;
  const executionCandidate = execution?.candidate ?? null;
  const executionJob = execution?.job ?? null;
  const candidateName = executionCandidate
    ? [executionCandidate.first_name, executionCandidate.middle_name, executionCandidate.last_name]
        .filter(Boolean)
        .join(" ")
    : "";

  return (
    <Page>
      <Page.Header
        titleSlot={
          execution ? (
            <DotSeparator className="text-lg">
              <Text className="font-semibold whitespace-nowrap text-ellipsis overflow-hidden">
                {execution.workflow_title}
              </Text>
            </DotSeparator>
          ) : undefined
        }
        showBack={true}
        navigateTo={"/workflows/executions"}
      >
        {execution && canCancel && (
          <Button
            variant={"secondary"}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-circle-stop"} />}
            onClick={() => {
              openModal(ConfirmationModal, {
                question: t("workflow:cancel_execution"),
                text: t("workflow:cancel_execution_confirm"),
                acceptTitle: t("workflow:cancel_execution_submit"),
                onAccept: async () => {
                  await cancelWorkflow(execution.id);
                },
              });
            }}
          >
            {t("workflow:cancel_execution_short")}
          </Button>
        )}
      </Page.Header>
      <Page.Content>
        {isLoading && <Skeleton className={"w-full h-32"} />}

        {execution && (
          <Stack gap={"lg"}>
            <Stack direction={"horizontal"} gap={"md"} className={"py-(--gap-lg)"}>
              <MetricTile
                className={"flex-1"}
                title={t("common:status")}
                value={getApiWorkflowExecutionStatusTitle(execution.status)}
              />
              {executionEmployee && (
                <MetricTile
                  className={"flex-1"}
                  title={t("core:employee")}
                  value=""
                  subValue={
                    <ListItem
                      title={renderEmployeeName(executionEmployee)}
                      avatarUrl={getEmployeeAvatarUrl(executionEmployee, "lg")}
                      forceShowAvatar={true}
                      behavior={"card"}
                      to={
                        executionEmployee.is_archived
                          ? undefined
                          : `/employees/${executionEmployee.id}`
                      }
                      onClick={(event: MouseEvent<HTMLElement>) => {
                        if (
                          executionEmployee.is_archived ||
                          event.metaKey ||
                          event.ctrlKey ||
                          event.shiftKey ||
                          event.altKey
                        ) {
                          return;
                        }

                        event.preventDefault();
                        openModal(EmployeeViewModal, { employeeId: executionEmployee.id });
                      }}
                    />
                  }
                />
              )}
              {executionCandidate && (
                <MetricTile
                  className={"flex-1"}
                  title={t("ats:candidate")}
                  value=""
                  subValue={
                    <ListItem
                      title={candidateName}
                      forceShowAvatar={true}
                      behavior={"card"}
                      to={`/recruitment/candidates/${executionCandidate.applicant_id}`}
                      blank
                    />
                  }
                />
              )}
              {executionJob && (
                <MetricTile
                  className={"flex-1"}
                  title={t("ats:job")}
                  value=""
                  subValue={
                    <ListItem
                      title={executionJob.title}
                      icon={<FontAwesomeIcon icon={"fa-light fa-briefcase"} />}
                      behavior={"card"}
                      to={`/recruitment/candidates/?job_id=${executionJob.id}`}
                      blank
                    />
                  }
                />
              )}
              <MetricTile
                className={"flex-1"}
                title={t("workflow:execution_started_at")}
                value={
                  execution.datetime_started_at
                    ? renderDateAndTime(execution.datetime_started_at)
                    : "-"
                }
              />
              <MetricTile
                className={"flex-1"}
                title={t("workflow:execution_duration")}
                value={duration}
              />
            </Stack>

            {execution.error_message && !hasFailedBlock && (
              <Box variant={"whiteBorder"} className={"w-full sm:container-sm mx-auto p-5"}>
                <ListItem
                  behavior={"card"}
                  emphasis={"top"}
                  titleSlot={
                    <Box className={"rounded-sm bg-dark/5 bg-(--color-danger-subtle)"}>
                      <Text size={"sm"} color={"danger"}>
                        {execution.error_message}
                      </Text>
                    </Box>
                  }
                  valueSlot={
                    <Stack items={"end"} gap={"xs"} className={"shrink-0"}>
                      {getWorkflowExecutionBlockStatusTag(ApiWorkflowExecutionBlockStatus.failed)}
                    </Stack>
                  }
                />
              </Box>
            )}

            {tree && (
              <Box className={"w-full sm:container-sm mx-auto"}>
                <Stack gap={"md"}>
                  <Title header={t("workflow:execution_steps")} paddingBottom />
                  <ExecutionBlockNode
                    node={tree}
                    executionBlocksMap={executionBlocksMap}
                    meetingsMap={meetingsMap}
                    isLast={true}
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Page.Content>
    </Page>
  );
};
